'use client';

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Figma desktop 303:3431 / mobile 303:3038.
 *
 * Mobile only shows two badges — "Award-winning app" is desktop-only (hidden below the
 * `dt` breakpoint via `desktopOnly`).
 */
const BADGES = [
  {
    src: '/images/comix/homepage/v2/trust-satisfaction.svg',
    label: 'Satisfaction Guaranteed',
    note: null,
    desktopOnly: false,
  },
  {
    src: '/images/comix/homepage/v2/trust-print-quality.svg',
    label: 'Best print quality',
    note: '(800 DPI)',
    desktopOnly: false,
  },
  {
    src: '/images/comix/homepage/v2/trust-award-app.svg',
    label: 'Award-winning app',
    note: null,
    desktopOnly: true,
  },
] as const;

/**
 * The badge seals scale up out of a slight counter-rotation as they fade in, one after
 * the other. `custom` is the seal's own start delay — see the stagger constants below.
 */
const SEAL_VARIANTS = {
  hidden: { opacity: 0, scale: 0.55, rotate: -18 },
  shown: (delay: number) => ({
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 220,
      damping: 15,
      delay,
      // Scale/rotate overshoot on purpose; opacity must not, or the seal visibly dips
      // back after reaching full strength.
      opacity: { type: 'tween' as const, duration: 0.35, delay },
    },
  }),
};

/**
 * Mobile stacks two seals that scroll past together, so they overlap — the second starts
 * while the first is still springing.
 */
const MOBILE_STAGGER = 0.14;

/**
 * Desktop shows all three side by side and reads them left to right, so they run in
 * sequence instead: 0.55s is roughly where the spring above has settled (ζ≈0.51, ω≈14.8
 * rad/s → ~0.53s to within 2%), so each seal lands just as the next lifts off. Shorter and
 * they blur together; longer and the row stalls between them.
 */
const DESKTOP_STAGGER = 0.55;

/**
 * A plain <img> and a motion one are deliberately separate elements rather than one
 * element with conditional props: framer reads `initial` only on mount, so a prop flip
 * afterwards would leave the seal stuck at rest. Swapping the element type remounts it.
 */
function BadgeSeal({
  badge,
  delay,
  animate,
}: {
  badge: (typeof BADGES)[number];
  delay: number;
  animate: boolean;
}) {
  // Exported with preserveAspectRatio="none", so the 1:1 box is load-bearing.
  // loading="lazy" on ALL badges: this whole section renders well below the fold (after
  // the hero, HowItWorks, and Community sections), yet without a lazy hint these plain
  // <img> tags were getting picked up by React's automatic image-preload promotion and
  // landing ahead of the actual hero preload in the rendered <head> — competing with,
  // rather than losing to, the real LCP image. (Originally only the desktop-only badge
  // was lazy, to skip fetching it while its <li> is display:none on mobile — that reason
  // still applies there too, it's just no longer the only one.)
  const props = {
    src: badge.src,
    alt: '',
    'aria-hidden': true,
    loading: 'lazy' as const,
    className: 'aspect-square w-[170px] max-w-full dt:w-[52%]',
  };

  if (!animate) return <img {...props} />;

  return (
    <motion.img
      {...props}
      variants={SEAL_VARIANTS}
      custom={delay}
      initial="hidden"
      whileInView="shown"
      viewport={{ once: true, amount: 0.5 }}
    />
  );
}

export default function TrustBadges() {
  const reduceMotion = useReducedMotion() ?? false;
  const isMobile = useIsMobile();
  const animate = !reduceMotion;
  const stagger = isMobile ? MOBILE_STAGGER : DESKTOP_STAGGER;

  return (
    <section className="w-full py-[var(--hp-section-y)] dt:pb-[80px] dt:pt-[30px]">
      <div className="hp-container">
        <h2
          className="text-center font-bold tracking-[-0.454px] text-[var(--hp-ink)]"
          style={{ fontSize: 'var(--hp-h2)', lineHeight: 1.35 }}
        >
          You&apos;re in good hands
        </h2>

        <ul className="mt-10 flex flex-col items-center gap-12 dt:mt-12 dt:flex-row dt:items-start dt:justify-between dt:gap-[2.4%]">
          {BADGES.map((badge, i) => (
            <li
              key={badge.label}
              className={`w-full max-w-[240px] flex-col items-center gap-2 dt:max-w-none dt:flex-1 ${badge.desktopOnly ? 'hidden dt:flex' : 'flex'}`}
            >
              <BadgeSeal badge={badge} delay={i * stagger} animate={animate} />
              <p
                className="flex flex-wrap items-end justify-center gap-x-[2px] text-center font-medium capitalize text-[var(--hp-body)]"
                style={{ fontSize: 'var(--hp-trust)' }}
              >
                {badge.label}
                {/* Same color as the label on both breakpoints; padding-bottom differs
                    (1px mobile, 2px desktop). */}
                {badge.note ? (
                  <span
                    className="pb-[1px] text-[var(--hp-body)] dt:pb-[2px]"
                    style={{ fontSize: '14px' }}
                  >
                    {badge.note}
                  </span>
                ) : null}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
